import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { OverflownText } from 'components/OverflownText'
import { SalesStage } from 'models/sales_stages'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { PiLock, PiMagnifyingGlass, PiPlus } from 'react-icons/pi'
import useApiService from 'services/api'
import { ClientDataTable } from '../../../components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import FbButton from '../../../components/FbUI/FbButton'
import { BootstrapInput } from '../../../components/FormUtils/NumericInput'
import { Modal } from '../../../components/UI/Modal/Modal'
import { handleFormError } from '../../../utils/handleFormError'
import { StagePill } from 'components/FbUI/StagePill'
import { ReorderStagesModal } from './ReorderStagesModal'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

const defaultValuesStage = {
  stage: '',
  win_rate: 0,
  avg_days_to_close: 0,
  description: '',
}

export function SalesStageTable() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const { dealDetailPage } = useFeatureFlagService()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [selectedStageID, setSelectedStageID] = useState<number>()
  const [searchQuery, setSearchQuery] = useState('')
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const {
    data: stageData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['stages'],
    queryFn: async () => {
      return await api.getSalesStages()
    },
  })

  const sortedData = useMemo(() => {
    return stageData?.toSorted((a, b) =>
      customOrderEnabled
        ? a.order - b.order
        : b.avg_days_to_close - a.avg_days_to_close
    )
  }, [stageData, customOrderEnabled])

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !sortedData) return sortedData
    const q = searchQuery.toLowerCase()
    return sortedData.filter(
      (item) =>
        item.stage?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q)
    )
  }, [sortedData, searchQuery])

  const {
    control: control,
    handleSubmit: handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
  } = useForm({
    defaultValues: defaultValuesStage,
    mode: 'onSubmit',
  })

  function openModal() {
    setShowModal(true)
  }

  function hideModal() {
    setShowModal(false)
    reset(defaultValuesStage)
    setSelectedStageID(undefined)
  }

  const onSubmitCreate = handleSubmit(async (values) => {
    const [err] = await to(
      api.postSalesStage({
        avg_days_to_close: values.avg_days_to_close,
        stage: values.stage,
        win_rate: values.win_rate,
        description: values.description,
      })
    )
    if (err) handleFormError(err, setError)

    await refetch()
    hideModal()
  })

  const onSubmitUpdate = handleSubmit(async (values) => {
    if (!selectedStageID) return

    const [err] = await to(
      api.putStage(selectedStageID, {
        avg_days_to_close: values.avg_days_to_close,
        stage: values.stage,
        win_rate: values.win_rate,
        description: values.description,
      })
    )
    if (err) handleFormError(err, setError)

    await refetch()

    hideModal()
  })

  async function handleRowDelete(index: number) {
    const stage = data?.[index]
    if (!stage?.id) return

    // Block deletion if stage is in use by deals or automations
    const warnings: string[] = []
    if (stage.deals_count > 0) {
      warnings.push(
        `${stage.deals_count} deal${stage.deals_count !== 1 ? 's' : ''}`
      )
    }
    if (stage.automations_count > 0) {
      warnings.push(
        `${stage.automations_count} automation${stage.automations_count !== 1 ? 's' : ''}`
      )
    }

    if (warnings.length > 0) {
      await dialogConfirm(
        `Stage "${stage.stage}" cannot be deleted because it is currently referenced by ${warnings.join(' and ')}.`,
        'Cannot Delete Stage',
        'Please reassign these references before deleting this stage.'
      )
      return
    }

    if (
      await dialogConfirm(
        'Are you sure you want to delete this stage?',
        'Delete Stage'
      )
    ) {
      try {
        await api.deleteStage(stage.id)
        void refetch()
        void queryClient.invalidateQueries({ queryKey: ['stages'] })
      } catch {
        // Error toast handled by baseAPICall
      }
    }
  }

  function handleRowEdit(index: number) {
    setShowModal(true)
    setSelectedStageID(data?.[index]?.id)
    if (data) reset(data?.[index])
  }

  async function handleBulkReorder(orderedStageIds: number[]) {
    await api.bulkReorderSalesStages(orderedStageIds)
    void refetch()
    void queryClient.invalidateQueries({ queryKey: ['stages'] })
  }

  const columns = useMemo<ColumnDef<SalesStage, any>[]>(
    () => [
      {
        accessorKey: 'stage',
        header: 'Stage Name',
        size: 200,
        meta: { isFirstColumn: true },
        cell: (info) => {
          return <StagePill stageId={info.row.original.id} />
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 400,
        cell: (info) => {
          const value = info.getValue()
          return <OverflownText maxLines={2}>{value}</OverflownText>
        },
      },
      {
        accessorKey: 'win_rate',
        header: 'Win Rate (%)',
        size: 130,
      },
      {
        accessorKey: 'avg_days_to_close',
        header: 'Avg Days to Close',
        size: 150,
      },
      {
        accessorKey: 'deals_count',
        header: 'Deals',
        size: 110,
        cell: ({ row }) => {
          if (row.original?.deals_count) {
            return row.original.deals_count.toLocaleString('en-US')
          }

          return 0
        },
      },
      {
        id: 'actions',
        header: () => <div className="w-full text-center">Actions</div>,
        size: 70,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
        },
        cell: ({ row }) => {
          if (
            row.original.stage === 'Closed Won' ||
            row.original.stage === 'Closed Lost' ||
            row.original.stage === 'Churn'
          )
            return (
              <div
                className="flex w-full justify-center text-gray-500"
                title="Required stage — cannot be edited or deleted"
              >
                <PiLock size={16} />
              </div>
            )
          return (
            <ActionsDropdown
              className="!w-full justify-center"
              actions={[
                createEditAction(() => handleRowEdit(row.index)),
                createDeleteAction(() => handleRowDelete(row.index)),
              ]}
            />
          )
        },
      },
    ],
    [data]
  )

  if (isLoading || !sortedData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          width: '100%',
        }}
      >
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <StageColorProvider value={dealDetailPage ? 'pipeline' : 'win_rate'}>
      <TableLinkStyleProvider variant="plain">
        <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
          <DataTableContainer className="h-full">
            <ClientDataTable
              tableHeader={
                <div
                  data-table-toolbar
                  className="flex flex-row items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="relative w-full max-w-md">
                    <PiMagnifyingGlass
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search stages..."
                      className="w-full h-9 pl-9 pr-3 text-sm bg-white border border-gray-300 rounded-lg text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                    />
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <ReorderStagesModal
                      stages={sortedData ?? []}
                      onSave={handleBulkReorder}
                    />
                    <FbButton
                      variant="primary"
                      size="sm"
                      onClick={() => openModal()}
                      className="!bg-brand-bright !text-white hover:!bg-brand-active"
                    >
                      <PiPlus size={16} />
                      Create Stage
                    </FbButton>
                  </div>
                </div>
              }
              height="100%"
              stickyLastColumn
              data={data ?? []}
              columns={columns}
            />
            <Modal
              open={showModal}
              title={selectedStageID ? 'Edit Sales Stage' : 'Create New Stage'}
              description={
                selectedStageID ? (
                  <>
                    Edit your sales stage to reflect your current process and
                    performance.
                    <br />
                    &quot;Note that changes may change LTV and revenue
                    estimates.
                  </>
                ) : (
                  <>
                    Create a sales stage to reflect your current process and its
                    performance.
                    <br />
                    Note that changes may change LTV and revenue estimates.
                  </>
                )
              }
              onOpenChange={(open) => !open && hideModal()}
              loading={isSubmitting}
              blockAccept={!isValid}
              onAccept={selectedStageID ? onSubmitUpdate : onSubmitCreate}
              acceptButtonText={selectedStageID ? 'Update' : 'Create'}
            >
              <BootstrapInput
                name="stage"
                control={control}
                type="text"
                label="Stage Name"
                placeholder="Sales Stage"
              />
              <BootstrapInput
                name="win_rate"
                control={control}
                type="text"
                label="Win Rate (%)"
                placeholder="Win Rate"
                units={'%'}
              />
              <BootstrapInput
                name="avg_days_to_close"
                control={control}
                type="text"
                label="Average Days to Close"
                placeholder="Average Days to Close"
              />
              <TextareaInput
                name="description"
                control={control}
                type="textarea"
                label="Description"
                placeholder="Description"
              />
            </Modal>
          </DataTableContainer>
        </div>
      </TableLinkStyleProvider>
    </StageColorProvider>
  )
}
